import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { PdfViewer } from '@/components/pdfViewer';
import { usePictureInPictureModeContext } from '@/features/approving_invoice_reports/components/List/context/PictureInPictureModeProvider';
import { usePaymentDetail } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/context/PaymentDetailContext';
import { useSupportDocumentPictureInPicture } from '@/hooks/useDocumentPictureInPicture';
import { Loading } from '@moneyforward/ap-frontend-components';
import { useGetMfFileAndObjectURL } from '@service/mf_file/MfFile.service';
import classnames from 'classnames/bind';
import { FC, memo, Suspense, useCallback, useEffect, useMemo } from 'react';
import { Header } from './Header';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

export type InvoicePreviewProps = {
  pdfViewerContainerRef: React.RefObject<HTMLDivElement>;
  handleOpenPictureInPicture: () => void;
  invoiceFileId: string;
  reportId: string;
  handleUpdatePictureInPictureContent: () => void;
};

const SuspenseLoading: FC = memo(() => {
  return (
    <div>
      <Loading className={cx(styles['spinner'])} size='large' />
    </div>
  );
});
SuspenseLoading.displayName = 'SuspenseLoading';

export const InvoicePreview: FC<InvoicePreviewProps> = memo((props) => {
  return (
    <SuspenseErrorBoundary>
      <Suspense fallback={<SuspenseLoading />}>
        <InnerInvoicePreview {...props} />
      </Suspense>
    </SuspenseErrorBoundary>
  );
});
InvoicePreview.displayName = 'InvoicePreview';

export const InnerInvoicePreview: FC<InvoicePreviewProps> = memo(
  ({
    pdfViewerContainerRef,
    handleOpenPictureInPicture,
    invoiceFileId,
    reportId,
    handleUpdatePictureInPictureContent,
  }) => {
    const { pdfViewerProps, setShowFileInfoDrawer } = usePaymentDetail();
    const isPiPSupport = useSupportDocumentPictureInPicture();
    const { isPIPMode } = usePictureInPictureModeContext();

    // Use useGetMfFileAndObjectURL to fetch PDF URL
    const { url: invoiceUrl } = useGetMfFileAndObjectURL(invoiceFileId || '');

    useEffect(() => {
      if (invoiceUrl && reportId && isPIPMode) {
        handleUpdatePictureInPictureContent();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceUrl, reportId]);

    const handleOpenFileInfoDrawer = useCallback(() => {
      setShowFileInfoDrawer(true);
    }, [setShowFileInfoDrawer]);
    const generateKey = useMemo(() => {
      return `invoice-${reportId}-${invoiceUrl}-${
        isPIPMode ? 'minimized' : 'preview'
      }`;
    }, [invoiceUrl, isPIPMode, reportId]);

    return (
      <div className={cx(styles['previewContainer'])}>
        {pdfViewerProps && (
          <Header
            {...pdfViewerProps}
            onPictureInPicture={handleOpenPictureInPicture}
            isPiPSupport={isPiPSupport}
            onOpenFileInfoDrawer={handleOpenFileInfoDrawer}
          />
        )}
        <div
          className={cx(styles['pdfViewerContainer'])}
          ref={pdfViewerContainerRef}
        >
          <PdfViewer key={generateKey} src={invoiceUrl} />
        </div>
      </div>
    );
  }
);
InnerInvoicePreview.displayName = 'InnerInvoicePreview';
